import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import Circle from '@arcgis/core/geometry/Circle';
import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import SketchWidget from '@arcgis/core/widgets/Sketch';
import {
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { GeolocationService } from 'app/shared/services/geolocation.service';
import { ScreenService } from 'app/shared/services/screen.service';
import { ToastService } from 'app/shared/services/toast.service';
import { TranslationService } from 'app/shared/services/translation.service';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { influenceArea } from '../../default-symbols';

export enum DrawingType {
  Polygon,
  FixedDistance,
  InteractiveDistance,
  Line,
  CurrentLocation,
}

interface DrawButonItemLike {
  type: DrawingType;
  icon: string;
  tooltip?: string;
  active?: boolean;
  hover?: boolean;
  defaultDistance?: number;
  maxDistance?: number;
}

@Component({
  selector: 'app-influence-area',
  templateUrl: 'influence-area.component.html',
  styleUrls: ['influence-area.component.scss'],
})
export class InfluenceAreaComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  mapView: __esri.MapView;
  @Input()
  searchGroupLayer: GroupLayer;

  @Output()
  onGeometryChanged = new EventEmitter<__esri.Geometry>(true);

  private sketchWidget: SketchWidget;
  private drawingLayer: GraphicsLayer;
  private graphicsLayer: GraphicsLayer;
  private _buttonList: DrawButonItemLike[] = [];

  get buttonList(): DrawButonItemLike[] {
    if (this._buttonList.length === 0) {
      this.initializeButtonList();
    }
    return this._buttonList;
  }

  private initializeButtonList() {
    const translations = this.translationService?.getCurrentLanguage();
    if (!translations) {
      // Retry later if translations are not ready
      setTimeout(() => this.initializeButtonList(), 100);
      return;
    }

    this._buttonList = [
      {
        type: DrawingType.Polygon,
        icon: 'assets/icons/draw-polygon.svg',
        tooltip: translations['polygon'],
      },
      {
        type: DrawingType.FixedDistance,
        icon: 'assets/icons/circle-notch.svg',
        tooltip: translations['fixed-distance'],
        defaultDistance: 20,
        maxDistance: 10000,
      },
      {
        type: DrawingType.InteractiveDistance,
        icon: 'assets/icons/circle-dashed.svg',
        tooltip: translations['interactive-distance'],
      },
      {
        type: DrawingType.Line,
        icon: 'assets/icons/line.svg',
        tooltip: translations['line'],
        defaultDistance: 30,
      },
      {
        type: DrawingType.CurrentLocation,
        icon: 'assets/icons/current-location.svg',
        tooltip: translations['current-location'],
        defaultDistance: 100,
      },
    ];
  }

  get distanceText(): string {
    return (
      this.translationService?.getCurrentLanguage()?.['distance'] || 'Distance'
    );
  }

  get metersText(): string {
    return (
      this.translationService?.getCurrentLanguage()?.['meters'] || 'meters'
    );
  }

  distance = 0;
  get maxDistance() {
    return this.activeItem?.maxDistance ?? 100;
  }

  private _activeItem: DrawButonItemLike;

  get activeItem() {
    return this._activeItem;
  }

  set activeItem(value) {
    if (this._activeItem && this._activeItem !== value) {
      this._activeItem.active = false;
    }

    this._activeItem = value;

    if (this._activeItem) {
      this._activeItem.active = true;
    }
  }

  private get activeType() {
    return this.activeItem?.type;
  }

  private geometry: __esri.Geometry;

  get isDistanceInputDisabled() {
    return !(
      this.activeType === DrawingType.FixedDistance ||
      this.activeType === DrawingType.Line
    );
  }

  private currentLocationRequestSubject = new Subject<boolean>();

  private mapViewSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private mainMapService: MainMapService,
    private geolocationService: GeolocationService,
    private toastService: ToastService,
    private screenService: ScreenService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    // Initialize button list with translations
    this.initializeButtonList();

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.reset();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView || changes.searchGroupLayer) {
      this.handleMapView();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.mapViewSubscription?.unsubscribe();
  }

  onBtnItemClick(e, item: DrawButonItemLike) {
    if (item.active) {
      this.activeItem = null;
    } else {
      this.activeItem = item;
    }

    this.handleDrawMode(this.activeType);
  }

  reset() {
    this.activeItem = null;

    this.sketchWidget?.cancel();
    this.graphicsLayer?.removeAll();

    if (this.geometry) {
      this.geometry = null;
      this.onGeometryChanged.emit(this.geometry);
    }
  }

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();

    if (!this.mapView) {
      return;
    }

    const subscription = new Subscription();

    const searchGroupLayer: __esri.LayersMixin =
      this.searchGroupLayer ?? this.mapView.map;

    this.graphicsLayer = new GraphicsLayer();
    searchGroupLayer.add(this.graphicsLayer);

    this.drawingLayer = new GraphicsLayer();
    searchGroupLayer.add(this.drawingLayer);

    const sketch = new SketchWidget({
      layer: this.drawingLayer,
      view: this.mapView,
      creationMode: 'single',
    });

    this.sketchWidget = sketch;

    const maxInterativeDistance = 50;

    const shouldUseDistance = () =>
      [DrawingType.FixedDistance, DrawingType.Line].includes(this.activeType);

    const handleInteractiveDistanceGeom = (geometry: __esri.Polygon) => {
      const radius = EsriUtils.calculateRadius(geometry);
      if (radius > maxInterativeDistance) {
        geometry = new Circle({
          center: geometry.centroid,
          radiusUnit: 'meters',
          radius: maxInterativeDistance,
          spatialReference: geometry.spatialReference,
        });
      }

      return geometry;
    };

    let lineGraphic: Graphic;
    const copyLineGeomIfCan = (geometry: __esri.Geometry) => {
      if (!(this.activeType === DrawingType.Line && this.distance > 0)) {
        return;
      }

      if (!geometry) {
        if (lineGraphic) {
          this.graphicsLayer.remove(lineGraphic);
          lineGraphic = null;
        }
      } else {
        if (
          !lineGraphic ||
          !this.graphicsLayer.graphics.includes(lineGraphic)
        ) {
          lineGraphic = new Graphic({
            symbol: influenceArea.polylineSymbol,
          });
          this.graphicsLayer.add(lineGraphic);
        }

        lineGraphic.geometry = geometry.clone();
      }
    };

    subscription.add(
      EsriUtils.on(sketch, 'create').subscribe(
        (evt: __esri.SketchCreateEvent) => {
          switch (evt.state) {
            case 'cancel':
              copyLineGeomIfCan(null);
              this.graphicsLayer?.removeAll();
              break;
            case 'active':
            case 'complete':
              if (evt.graphic) {
                if (shouldUseDistance()) {
                  copyLineGeomIfCan(evt.graphic.geometry);

                  const distance = Math.min(
                    this.distance ?? 0,
                    this.maxDistance
                  );
                  if (distance > 0) {
                    const geometry = EsriUtils.geodesicBuffer(
                      evt.graphic.geometry,
                      distance
                    );

                    evt.graphic.geometry = geometry;
                  }

                  if (this.activeType === DrawingType.Line) {
                    evt.graphic.symbol =
                      distance > 0
                        ? influenceArea.polygonFillWithoutOutlineSymbol
                        : influenceArea.polylineSymbol;
                  } else {
                    evt.graphic.symbol = influenceArea.polygonFillSymbol;
                  }
                } else {
                  switch (this.activeType) {
                    case DrawingType.Polygon:
                      evt.graphic.symbol = influenceArea.polygonFillSymbol;
                      break;
                    case DrawingType.InteractiveDistance:
                      evt.graphic.geometry = handleInteractiveDistanceGeom(
                        evt.graphic.geometry as any
                      );
                      evt.graphic.symbol = influenceArea.polygonDashedSymbol;
                      break;
                  }
                }
              }

              if (evt.state === 'complete') {
                if (
                  evt.graphic &&
                  !this.graphicsLayer.graphics.includes(evt.graphic)
                ) {
                  this.graphicsLayer.add(evt.graphic);
                }

                this._emitGeometry(evt.graphic?.geometry);
              }

              break;
          }
        }
      )
    );

    subscription.add(
      this.currentLocationRequestSubject
        .pipe(
          switchMap((active) => {
            if (!active) {
              return EMPTY;
            }

            return this.geolocationService.getCurrentLocation().pipe(
              tap({
                next: (res) => {
                  if (!res?.coords) {
                    return;
                  }

                  const point = new Point(res.coords);
                  this.mainMapService.goTo(point);

                  const geometry = EsriUtils.geodesicBuffer(
                    point,
                    this.activeItem?.defaultDistance ?? 0
                  );

                  const graphic = new Graphic({
                    geometry,
                    symbol: influenceArea.polygonFillSymbol,
                  });
                  this.graphicsLayer.add(graphic);

                  this._emitGeometry(geometry);
                },
                error: (err) => {
                  this.toastService.error(
                    this.translationService.getCurrentLanguage()[
                      'unable-retrieve-current-location'
                    ]
                  );
                },
              })
            );
          })
        )
        .subscribe()
    );

    subscription.add(() => {
      this.handleDrawMode(null);
      this.sketchWidget?.destroy();
      searchGroupLayer?.remove(this.graphicsLayer);
      this.graphicsLayer?.destroy();
      searchGroupLayer?.remove(this.drawingLayer);
      this.drawingLayer?.destroy();
    });

    this.mapViewSubscription = subscription;
  }

  private handleDrawMode(type?: DrawingType) {
    this.sketchWidget?.cancel();
    this.graphicsLayer?.removeAll();
    this.drawingLayer?.removeAll();

    this.currentLocationRequestSubject.next(false);

    this.distance = this.activeItem?.defaultDistance ?? 0;

    switch (type) {
      case DrawingType.Polygon:
        this.sketchWidget?.create('polygon');
        break;
      case DrawingType.InteractiveDistance:
        this.sketchWidget?.create('circle');
        break;
      case DrawingType.FixedDistance:
        this.sketchWidget?.create('point');
        break;
      case DrawingType.Line:
        this.sketchWidget?.create('polyline');
        break;
      case DrawingType.CurrentLocation:
        this.currentLocationRequestSubject.next(true);
        break;
    }
  }

  private _emitGeometry(geometry: __esri.Geometry) {
    this.activeItem = null;
    this.geometry = geometry ?? null;
    this.onGeometryChanged.emit(this.geometry);
  }
}
